"use client"

import { useEffect } from "react"

/**
 * 404 analytics beacon — fires a `page_not_found` event in PostHog and a
 * warning-level message in Sentry whenever a not-found page renders.
 *
 * Why an inline `<script>` instead of `useEffect`:
 * the not-found surfaces server-render but DO NOT client-hydrate under
 * Next 16 cacheComponents for direct landings (typo / broken external
 * link / crawler — the common case). React effects never fire, so the
 * previous `useEffect(() => track.pageNotFound(...))` missed every real
 * 404. An inline script runs during HTML parse, regardless of hydration.
 *
 * The script polls until `window.posthog.__loaded` (the SDK chunk can
 * land slightly after the HTML) and then fires once. Sentry uses a
 * constant `fingerprint: ["404"]` so every 404 hit groups into a single
 * Sentry issue — alert on issue event-count to get "more than N 404s
 * per hour" thresholds. The path, surface, country, locale ride along
 * as tags/extras so the issue is drilldownable.
 *
 * Where the globals come from: `window.Sentry` is the full `@sentry/nextjs`
 * namespace pinned by `instrumentation-client.ts` right after `Sentry.init`
 * (the SDK itself only auto-mounts a replay-helper stub); `window.posthog`
 * is the consent-gated singleton pinned by `initPostHog` (`lib/posthog.ts`),
 * which `instrumentation-client.ts` also invokes at idle — the
 * `PostHogProvider` effect alone would never run on these non-hydrating
 * pages. Sentry is set synchronously before parse; PostHog lands at idle,
 * inside the poll budget.
 *
 * `pnf_source` classification (internal / ad / external, see
 * lib/not-found-source.ts): PostHog gets ALL 404s unconditionally
 * (findability analytics + the search-zero-results pipeline depend on the
 * full stream). Sentry only gets `internal` (broken link on our own site)
 * and `ad` (a paid-campaign destination pointed at a broken URL — Nick
 * wants these, a misconfigured ad is our bug too) — `external` (scanner/
 * bot/random-typo noise, which was 61% of Sentry's 404 volume) never
 * reaches Sentry. This script can't `import` the lib (it's a stringified
 * IIFE), so the classifier logic below is a manually-kept-in-sync copy —
 * guarded by the drift test in not-found-beacon.test.tsx.
 *
 * The inline `<script>` only runs when it arrives in the server HTML. The
 * locale 404 (`/ch/en/<missing>`) bails out to a full client render — its
 * SSR `<body>` is empty — and a `<script>` React inserts on the client is
 * inert, so the mount effect re-injects the same body as a DOM-created
 * script, which does execute. `window.__pnfFired` keeps it to one fire when
 * both paths run.
 */

export function notFoundBeaconScript(surface: "root" | "locale"): string {
  return `
(function () {
  if (window.__pnfFired) return;
  window.__pnfFired = true;

  var SURFACE = ${JSON.stringify(surface)};

  var AD_CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'dclid', 'fbclid', 'msclkid', 'ttclid', 'twclid', 'li_fat_id', 'epik', 'sccid', 'irclickid', 'yclid'];
  var AD_REFERRER_HOSTS = ['google.', 'bing.', 'facebook.', 'instagram.', 'fb.', 'doubleclick.', 'tiktok.', 't.co', 'linkedin.', 'pinterest.', 'reddit.', 'snapchat.'];

  function referrerHostname(referrer) {
    try {
      return new URL(referrer).hostname;
    } catch (e) {
      return undefined;
    }
  }

  function isInternalReferrer(referrer, hostname) {
    var refHost = referrerHostname(referrer);
    if (!refHost) return false;
    return refHost === hostname || refHost === 'aheadhealth.com' || (refHost.length >= '.aheadhealth.com'.length && refHost.slice(-'.aheadhealth.com'.length) === '.aheadhealth.com');
  }

  function hasAdMarker(search) {
    var params = new URLSearchParams(search);
    var keys = Array.from(params.keys());
    for (var i = 0; i < keys.length; i++) {
      var lower = keys[i].toLowerCase();
      if (lower.indexOf('utm_') === 0) return true;
      for (var j = 0; j < AD_CLICK_ID_PARAMS.length; j++) {
        if (AD_CLICK_ID_PARAMS[j].toLowerCase() === lower) return true;
      }
    }
    return false;
  }

  function isAdReferrer(referrer) {
    var refHost = referrerHostname(referrer);
    if (!refHost) return false;
    for (var i = 0; i < AD_REFERRER_HOSTS.length; i++) {
      if (refHost.indexOf(AD_REFERRER_HOSTS[i]) !== -1) return true;
    }
    return false;
  }

  function classifySource(referrer, hostname, search) {
    if (referrer && isInternalReferrer(referrer, hostname)) return 'internal';
    if (hasAdMarker(search)) return 'ad';
    if (referrer && isAdReferrer(referrer)) return 'ad';
    return 'external';
  }

  function detectLocale() {
    var seg = window.location.pathname.split('/').filter(Boolean);
    var c = seg[0] || '';
    var l = seg[1] || '';
    var valid = { ch: ['en','de','fr'], nl: ['nl','en'], de: ['de'] };
    if (valid[c] && valid[c].indexOf(l) !== -1) return { country: c, locale: l };
    return { country: undefined, locale: undefined };
  }

  function fire() {
    var meta = detectLocale();
    var path = window.location.pathname;
    var pnfSource = classifySource(document.referrer, window.location.hostname, window.location.search);
    var props = {
      path: path,
      full_url: window.location.href,
      referrer: document.referrer || undefined,
      surface: SURFACE,
      country: meta.country,
      locale: meta.locale,
      pnf_source: pnfSource,
    };

    try {
      if (window.posthog && window.posthog.__loaded) {
        window.posthog.capture('page_not_found', props);
      }
    } catch (e) { console.warn('[not-found-beacon]', e) }

    try {
      if (pnfSource !== 'external' && window.Sentry && typeof window.Sentry.captureMessage === 'function') {
        window.Sentry.captureMessage('404: ' + path, {
          level: 'warning',
          tags: {
            surface: SURFACE,
            country: meta.country || 'unknown',
            locale: meta.locale || 'unknown',
            page_not_found: 'true',
            pnf_source: pnfSource,
          },
          extra: {
            path: path,
            full_url: window.location.href,
            referrer: document.referrer || '',
          },
          fingerprint: ['page-not-found'],
        });
      }
    } catch (e) { console.warn('[not-found-beacon]', e) }
  }

  var attempts = 0;
  function tick() {
    var phReady = window.posthog && window.posthog.__loaded;
    var sentryReady = !!window.Sentry;
    if ((phReady && sentryReady) || attempts > 50) {
      fire();
      return;
    }
    attempts++;
    setTimeout(tick, 100);
  }
  tick();
})();
`.trim()
}

export function NotFoundBeacon({ surface }: { surface: "root" | "locale" }) {
  const body = notFoundBeaconScript(surface)

  useEffect(() => {
    const script = document.createElement("script")
    script.text = body
    document.head.appendChild(script)
  }, [body])

  return <script dangerouslySetInnerHTML={{ __html: body }} />
}
